import { supabase } from '../lib/supabase';
import { SessionDetails, Squad, Attendance, CoachAssignment, DocumentType, emptySquadAttendance, emptySquadCoaches, emptySquadFocus } from '../types';

// Local types for Supabase join result shapes
type SquadRef = { id: string; name: string; display_name: string };
type SessionSquadJoin = { squad_id: string; squads: SquadRef; focus: string | null };
type SessionCoachJoin = { squad_id: string; primary_coach_id: string | null; secondary_coach_id: string | null; squads: SquadRef };
type DocJoin = { id: string; name: string; type: string; url: string; size: number; uploaded_at: string };

export const getSessions = async (): Promise<SessionDetails[]> => {
  try {
    // Get all sessions with their related data
    const { data: sessions, error } = await supabase
      .from('sessions')
      .select(`
        *,
        session_squads (
          squad_id,
          squads (
            id,
            name,
            display_name
          ),
          focus
        ),
        documents (*)
      `)
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }

    if (!sessions) {
      return [];
    }

    // Transform the data to match our SessionDetails type
    return sessions.map(session => ({
      id: session.id,
      date: session.date,
      time: session.time,
      duration: session.duration,
      location: session.location,
      type: session.session_type,
      groundBooking: session.ground_booking,
      squads: (session.session_squads as SessionSquadJoin[]).map(ss => ss.squads.name as Squad),
      attendance: emptySquadAttendance(),
      coaches: emptySquadCoaches(),
      focus: (session.session_squads as SessionSquadJoin[]).reduce((acc: Record<Squad, string>, ss) => {
        if (ss.squads?.name) {
          acc[ss.squads.name as Squad] = ss.focus || '';
        }
        return acc;
      }, emptySquadFocus()),
      documents: (session.documents as DocJoin[] | undefined)?.map(doc => ({
        id: doc.id,
        name: doc.name,
        type: doc.type as DocumentType,
        url: doc.url,
        size: doc.size,
        uploadedAt: doc.uploaded_at
      })) || [],
      attendanceStatus: session.attendance_status,
      locked: session.locked,
      trialPlayers: session.trial_players || []
    }));
  } catch (error) {
    console.error('Error in getSessions:', error);
    throw error;
  }
};

export const getSessionById = async (id: string): Promise<SessionDetails | null> => {
  try {
    // Get session with related data
    const { data: session, error } = await supabase
      .from('sessions')
      .select(`
        *,
        session_squads (
          squad_id,
          squads (
            id,
            name,
            display_name
          ),
          focus
        ),
        session_coaches (
          squad_id,
          primary_coach_id,
          secondary_coach_id,
          squads (
            id,
            name,
            display_name
          )
        ),
        documents (*)
      `)
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching session:', error);
      throw error;
    }

    if (!session) {
      return null;
    }

    // Transform and return the session data
    return {
      id: session.id,
      date: session.date,
      time: session.time,
      duration: session.duration,
      location: session.location,
      type: session.session_type,
      groundBooking: session.ground_booking,
      squads: (session.session_squads as SessionSquadJoin[]).map(ss => ss.squads.name as Squad),
      attendance: emptySquadAttendance(),
      coaches: (session.session_coaches as SessionCoachJoin[]).reduce((acc: Record<Squad, CoachAssignment>, sc) => {
        if (sc.squads?.name) {
          acc[sc.squads.name as Squad] = {
            primary: sc.primary_coach_id || '',
            secondary: sc.secondary_coach_id || ''
          };
        }
        return acc;
      }, emptySquadCoaches()),
      focus: (session.session_squads as SessionSquadJoin[]).reduce((acc: Record<Squad, string>, ss) => {
        if (ss.squads?.name) {
          acc[ss.squads.name as Squad] = ss.focus || '';
        }
        return acc;
      }, emptySquadFocus()),
      documents: (session.documents as DocJoin[] | undefined)?.map(doc => ({
        id: doc.id,
        name: doc.name,
        type: doc.type as DocumentType,
        url: doc.url,
        size: doc.size,
        uploadedAt: doc.uploaded_at
      })) || [],
      attendanceStatus: session.attendance_status,
      locked: session.locked,
      trialPlayers: session.trial_players || []
    };
  } catch (error) {
    console.error('Error in getSessionById:', error);
    throw error;
  }
};

export const createSession = async (session: Omit<SessionDetails, 'id'>): Promise<SessionDetails> => {
  try {
    // First, create the session
    const { data: newSession, error: sessionError } = await supabase
      .from('sessions')
      .insert({
        date: session.date,
        time: session.time,
        duration: session.duration,
        location: session.location,
        session_type: session.type,
        ground_booking: session.groundBooking,
        attendance_status: session.attendanceStatus,
        locked: session.locked || false,
        trial_players: session.trialPlayers || []
      })
      .select()
      .single();

    if (sessionError) {
      console.error('Error creating session:', sessionError);
      throw sessionError;
    }

    if (!newSession) {
      throw new Error('No session data returned after creation');
    }

    // Get squad IDs
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id, name')
      .in('name', session.squads);

    if (squadError) {
      console.error('Error fetching squad IDs:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('No squads found');
    }

    // Create squad associations with focus areas
    const squadInserts = squadData.map(squad => ({
      session_id: newSession.id,
      squad_id: squad.id,
      focus: session.focus[squad.name as Squad] || null
    }));

    const { error: squadsError } = await supabase
      .from('session_squads')
      .insert(squadInserts);

    if (squadsError) {
      console.error('Error creating session squads:', squadsError);
      throw squadsError;
    }

    // Create document records if any
    if (session.documents.length > 0) {
      const documentInserts = session.documents.map(doc => ({
        session_id: newSession.id,
        name: doc.name,
        type: doc.type,
        url: doc.url,
        size: doc.size,
        uploaded_at: doc.uploadedAt
      }));

      const { error: documentsError } = await supabase
        .from('documents')
        .insert(documentInserts);

      if (documentsError) {
        console.error('Error creating session documents:', documentsError);
        throw documentsError;
      }
    }

    // Return the complete session by fetching it with all relations
    return await getSessionById(newSession.id) as SessionDetails;
  } catch (error) {
    console.error('Error in createSession:', error);
    throw error;
  }
};

export const updateSession = async (id: string, session: Omit<SessionDetails, 'id'>): Promise<SessionDetails> => {
  try {
    // First, update the session
    const { data: updatedSession, error: sessionError } = await supabase
      .from('sessions')
      .update({
        date: session.date,
        time: session.time,
        duration: session.duration,
        location: session.location,
        session_type: session.type,
        ground_booking: session.groundBooking,
        attendance_status: session.attendanceStatus,
        locked: session.locked,
        trial_players: session.trialPlayers || []
      })
      .eq('id', id)
      .select()
      .single();

    if (sessionError) {
      console.error('Error updating session:', sessionError);
      throw sessionError;
    }

    if (!updatedSession) {
      throw new Error('No session data returned after update');
    }

    // Get squad IDs
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id, name')
      .in('name', session.squads);

    if (squadError) {
      console.error('Error fetching squad IDs:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error('No squads found');
    }

    // Update squad associations
    // First, delete existing
    const { error: deleteSquadsError } = await supabase
      .from('session_squads')
      .delete()
      .eq('session_id', id);

    if (deleteSquadsError) {
      console.error('Error deleting session squads:', deleteSquadsError);
      throw deleteSquadsError;
    }

    // Then insert new ones
    const squadInserts = squadData.map(squad => ({
      session_id: id,
      squad_id: squad.id,
      focus: session.focus[squad.name as Squad] || null
    }));

    const { error: squadsError } = await supabase
      .from('session_squads')
      .insert(squadInserts);

    if (squadsError) {
      console.error('Error updating session squads:', squadsError);
      throw squadsError;
    }

    // Update documents if changed
    // First, delete existing
    const { error: deleteDocsError } = await supabase
      .from('documents')
      .delete()
      .eq('session_id', id);

    if (deleteDocsError) {
      console.error('Error deleting session documents:', deleteDocsError);
      throw deleteDocsError;
    }

    // Then insert new ones
    if (session.documents.length > 0) {
      const documentInserts = session.documents.map(doc => ({
        session_id: id,
        name: doc.name,
        type: doc.type,
        url: doc.url,
        size: doc.size,
        uploaded_at: doc.uploadedAt
      }));

      const { error: documentsError } = await supabase
        .from('documents')
        .insert(documentInserts);

      if (documentsError) {
        console.error('Error updating session documents:', documentsError);
        throw documentsError;
      }
    }

    // Return the complete updated session
    return await getSessionById(id) as SessionDetails;
  } catch (error) {
    console.error('Error in updateSession:', error);
    throw error;
  }
};

export const deleteSession = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('sessions')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in deleteSession:', error);
    throw error;
  }
};

export const markSessionAttendance = async (
  sessionId: string,
  squad: Squad,
  attendance: Attendance[],
  coaches: CoachAssignment,
  trialPlayers?: { name: string; comments: string }[]
): Promise<void> => {
  try {
    // Get squad ID first
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('id')
      .eq('name', squad)
      .single();

    if (squadError) {
      console.error('Error fetching squad ID:', squadError);
      throw squadError;
    }

    if (!squadData) {
      throw new Error(`Squad not found: ${squad}`);
    }

    // Get session_squad_id
    const { data: sessionSquad, error: sessionSquadError } = await supabase
      .from('session_squads')
      .select('id')
      .eq('session_id', sessionId)
      .eq('squad_id', squadData.id)
      .single();

    if (sessionSquadError) {
      console.error('Error fetching session squad:', sessionSquadError);
      throw sessionSquadError;
    }

    if (!sessionSquad) {
      throw new Error(`Session squad not found for session ${sessionId} and squad ${squad}`);
    }

    // First, update coach assignments
    const { error: coachError } = await supabase
      .from('session_coaches')
      .upsert({
        session_id: sessionId,
        squad_id: squadData.id,
        primary_coach_id: coaches.primary || null,
        secondary_coach_id: coaches.secondary || null
      });

    if (coachError) {
      console.error('Error updating coach assignments:', coachError);
      throw coachError;
    }

    // Then, handle attendance records
    // First delete existing attendance records for this squad
    const { error: deleteError } = await supabase
      .from('attendance')
      .delete()
      .eq('session_id', sessionId)
      .eq('squad_id', squadData.id)
      .eq('session_squad_id', sessionSquad.id);

    if (deleteError) {
      console.error('Error deleting existing attendance:', deleteError);
      throw deleteError;
    }

    // Filter out any non-UUID player IDs (like trial players)
    const validAttendance = attendance.filter(record =>
      record.playerId !== 'trial-players' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(record.playerId)
    );

    // Insert new attendance records
    if (validAttendance.length > 0) {
      const attendanceInserts = validAttendance.map(record => ({
        session_id: sessionId,
        squad_id: squadData.id,
        session_squad_id: sessionSquad.id,
        player_id: record.playerId,
        present: record.present,
        comments: record.comments
      }));

      const { error: attendanceError } = await supabase
        .from('attendance')
        .insert(attendanceInserts);

      if (attendanceError) {
        console.error('Error marking attendance:', attendanceError);
        throw attendanceError;
      }
    }

    // Update trial players in the session table
    if (trialPlayers && trialPlayers.length > 0) {
      const { error: trialPlayersError } = await supabase
        .from('sessions')
        .update({ trial_players: trialPlayers })
        .eq('id', sessionId);

      if (trialPlayersError) {
        console.error('Error updating trial players:', trialPlayersError);
        throw trialPlayersError;
      }
    }

  } catch (error) {
    console.error('Error in markSessionAttendance:', error);
    throw error;
  }
};

export const updateSessionAttendanceStatus = async (
  sessionId: string,
  status: 'not-required' | 'pending' | 'marked'
): Promise<void> => {
  try {
    const { error } = await supabase
      .from('sessions')
      .update({ attendance_status: status })
      .eq('id', sessionId);

    if (error) {
      console.error('Error updating attendance status:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error in updateSessionAttendanceStatus:', error);
    throw error;
  }
};